import { Box } from "@material-ui/core";
import PropTypes from "prop-types";
import * as React from "react";
import { Helmet } from "react-helmet-async";
import { useHistory } from "react-router-dom";
import {
  GroupShopInfluencers,
  ShopHeader,
  ShopHeaderWithNoProduct,
  ShopProducts,
} from "../../components/Shop";
import GroupShopArtists from "../../components/Shop/GroupShopArtists";
import { Influencer } from "../../domain";
import { useAutoScroll, useJsonLd } from "../../hooks";
import { PageContainer, StreamilyTabs } from "../../theme";
import Recommendations from "./Recommendations";

const displayOptionsFor = (influencer) =>
  ["Products", "Talent", influencer.showArtists ? "Artists" : null].filter(
    Boolean,
  );

export default function Shop({ influencer }) {
  const history = useHistory();
  const { enableAutoScroll, disableAutoScroll } = useAutoScroll();

  const [currentTab, setCurrentTab] = React.useState(0);

  React.useEffect(() => {
    if (!influencer?.isGroupShop && currentTab !== 0) {
      setCurrentTab(0);
    }
  }, [currentTab, influencer]);

  React.useEffect(() => {
    disableAutoScroll();

    return () => {
      enableAutoScroll();
    };
  }, [disableAutoScroll, enableAutoScroll]);

  const setProductCallback = React.useCallback(
    (product) => {
      if (product?.productId) {
        history.push(`/${influencer.route}/${product.productId}`);
      }
    },
    [history, influencer],
  );

  const influencerDescription = [influencer.name, influencer.caption]
    .filter(Boolean)
    .join(": ");

  if (!influencer) return null;

  return (
    <PageContainer>
      <Helmet>
        <title>Streamily | {influencer.name}</title>

        <meta name="description" content={influencerDescription} />

        {Boolean(influencer.metaTag) && (
          <meta name="keywords" content={influencer.metaTag.famousFor} />
        )}
      </Helmet>

      {influencer?.products?.length && <ShopHeader influencer={influencer} />}

      {!influencer?.products?.length && (
        <ShopHeaderWithNoProduct influencer={influencer} />
      )}

      {influencer?.isGroupShop && (
        <Box width="100%" display="flex" justifyContent="center">
          <StreamilyTabs
            options={displayOptionsFor(influencer)}
            currentTab={currentTab}
            onChange={setCurrentTab}
            size="lg"
          />
        </Box>
      )}

      {currentTab === 0 &&
        (!influencer?.isOpen || !influencer?.products?.length) && (
          <Recommendations route={influencer.route} />
        )}

      {currentTab === 0 && influencer?.isOpen && (
        <ShopProducts influencer={influencer} setProduct={setProductCallback} />
      )}

      {currentTab === 1 && influencer?.isGroupShop && (
        <GroupShopInfluencers influencer={influencer} />
      )}

      {currentTab === 2 &&
        influencer?.isGroupShop &&
        influencer?.showArtists && <GroupShopArtists influencer={influencer} />}

      {influencer && <MetaData influencer={influencer} />}
    </PageContainer>
  );
}

Shop.propTypes = {
  influencer: PropTypes.instanceOf(Influencer),
};

function MetaData({ influencer }) {
  useJsonLd({
    "@context": "http://schema.org/",
    "@type": "Person",
    name: influencer.name,
  });

  return null;
}

MetaData.propTypes = {
  influencer: PropTypes.instanceOf(Influencer),
};
